/**
 * useShopPriceRange — dynamic price slider bounds for the shop list.
 *
 * Refetches `/shop/price-range` whenever the selected category set
 * changes. When `categoryIds` is empty the endpoint returns the
 * catalog-wide range (same data the filter-meta seed produced on
 * mount); when non-empty it returns the MIN/MAX across products IN
 * the selected categories only, so the slider re-seeds to a more
 * relevant range.
 *
 * Failure or while-loading: returns nulls so the slider section can
 * fall back to the seed values the filter-meta hook already cached.
 */

import { useEffect, useMemo, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'
import type { ShopPriceRangeDto } from './useShopFilterMeta'

interface Body {
  readonly success: true
  readonly data: { readonly priceRange: ShopPriceRangeDto }
}

export interface UseShopPriceRangeReturn {
  readonly priceRange: ShopPriceRangeDto
  readonly isLoading: boolean
  readonly error: string | null
}

export function useShopPriceRange(
  categoryIds: ReadonlyArray<string>
): UseShopPriceRangeReturn {
  const [priceRange, setPriceRange] = useState<ShopPriceRangeDto>({
    min_paisa: null,
    max_paisa: null
  })
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Stable key so the effect doesn't fire on every render when the
  // parent passes a fresh array of the same ids.
  const key = useMemo(() => [...categoryIds].sort().join(','), [categoryIds])

  useEffect(() => {
    let cancelled = false
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    const url =
      key.length === 0
        ? `${APIBASE}${APIROUTES.SHOPPRICERANGE}`
        : `${APIBASE}${APIROUTES.SHOPPRICERANGE}?categoryIds=${encodeURIComponent(key)}`
    ;(async () => {
      try {
        let res: Response
        try {
          res = await fetch(url, { signal: ac.signal })
        } catch (err) {
          if (cancelled) return
          if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
          throw err
        }
        if (cancelled) return
        if (res.status >= 500 || !res.ok) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        const body = (await res.json()) as Body
        if (!cancelled) setPriceRange(body.data.priceRange)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true; ac.abort() }
  }, [key])

  return { priceRange, isLoading, error }
}
